import { Download, FileSearch, ShieldAlert } from "lucide-react";
import type { AccessLogFilters, AccessLogPage } from "@/hooks/use-access";
import { accessLogsCsvUrl } from "@/hooks/use-access";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  data?: AccessLogPage;
  isLoading: boolean;
  filters: AccessLogFilters;
  onFiltersChange: (filters: AccessLogFilters) => void;
  onExport: () => void;
}

/** Etiquetas legibles: el panel no debe obligar a leer `document.download`. */
const ACTION_LABELS: Record<string, string> = {
  "auth.login": "Inicio de sesión",
  "auth.login_failed": "Login fallido",
  "auth.logout": "Cierre de sesión",
  "access.denied": "Acceso denegado",
  "access.unauthorized": "Sin autenticar",
  "document.download": "Descarga de documento",
  "document.upload": "Subida de documento",
  "document.delete": "Borrado de documento",
  "proposal.export": "Exportación de propuesta",
  "user.activated": "Usuario activado",
  "user.deactivated": "Usuario desactivado",
  "audit.export": "Exportación del registro",
  "section.generate": "Generación de sección",
};

const ACTION_OPTIONS = Object.entries(ACTION_LABELS);

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function outcomeTone(outcome: string): string {
  if (outcome === "success") return "bg-green-50 text-green-700";
  if (outcome === "denied") return "bg-amber-50 text-amber-700";
  return "bg-red-50 text-red-700";
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function AccessLogTable({
  data,
  isLoading,
  filters,
  onFiltersChange,
  onExport,
}: Props) {
  const page = filters.page ?? 1;
  const pageSize = filters.page_size ?? 50;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const update = (patch: Partial<AccessLogFilters>) =>
    // Al cambiar un filtro se vuelve a la página 1: si no, se quedaría en una
    // página que ya no existe y la tabla saldría vacía sin explicación.
    onFiltersChange({ ...filters, ...patch, page: patch.page ?? 1 });

  return (
    <div className="space-y-4">
      {/* Filtros. Van en la URL (los gestiona la página) para poder compartir
          el enlace exacto durante un incidente. */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col">
          <label htmlFor="filtro-accion" className="mb-1 text-xs font-medium text-gray-600">
            Acción
          </label>
          <select
            id="filtro-accion"
            value={filters.action ?? ""}
            onChange={(e) => update({ action: e.target.value || undefined })}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">Todas</option>
            {ACTION_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="filtro-categoria" className="mb-1 text-xs font-medium text-gray-600">
            Categoría
          </label>
          <select
            id="filtro-categoria"
            value={filters.category ?? ""}
            onChange={(e) => update({ category: e.target.value || undefined })}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">Todas</option>
            <option value="security">Seguridad</option>
            <option value="activity">Actividad</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="filtro-desde" className="mb-1 text-xs font-medium text-gray-600">
            Desde
          </label>
          <input
            id="filtro-desde"
            type="date"
            value={filters.date_from?.slice(0, 10) ?? ""}
            onChange={(e) =>
              update({ date_from: e.target.value ? `${e.target.value}T00:00:00` : undefined })
            }
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>

        <div className="flex flex-1 flex-col">
          <label htmlFor="filtro-texto" className="mb-1 text-xs font-medium text-gray-600">
            Buscar
          </label>
          <input
            id="filtro-texto"
            type="search"
            placeholder="Usuario, documento o ruta…"
            value={filters.q ?? ""}
            onChange={(e) => update({ q: e.target.value || undefined })}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>

        <a
          href={accessLogsCsvUrl()}
          onClick={(e) => {
            e.preventDefault();
            onExport();
          }}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </a>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : !data?.data.length ? (
        <EmptyState
          icon={<FileSearch />}
          title="Sin accesos registrados"
          description="No hay eventos que coincidan con los filtros seleccionados."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Fecha</th>
                  <th className="px-4 py-3 text-left font-medium">Usuario</th>
                  <th className="px-4 py-3 text-left font-medium">Acción</th>
                  <th className="px-4 py-3 text-left font-medium">Entidad</th>
                  <th className="px-4 py-3 text-left font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-gray-900">{item.actor_label || "—"}</span>
                      {item.actor_verified === false && (
                        <span
                          title="La identidad no pudo verificarse: proviene de un token no validado"
                          className="ml-2 inline-flex items-center gap-1 text-xs text-amber-600"
                        >
                          <ShieldAlert className="h-3 w-3" />
                          sin verificar
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${outcomeTone(
                          item.outcome
                        )}`}
                      >
                        {actionLabel(item.action)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {item.entity_label || item.entity_id || "—"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">
                      {item.ip || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {total} evento{total === 1 ? "" : "s"} · página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onFiltersChange({ ...filters, page: page - 1 })}
                disabled={page <= 1}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => onFiltersChange({ ...filters, page: page + 1 })}
                disabled={page >= totalPages}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
